<template lang="pug">
  .design-system-docs
    // Mobile Header with Menu Toggle
    .mobile-header.d-md-none.pa-3.d-flex.align-center.justify-space-between(style="background-color: #f8f9fa; border-bottom: 1px solid #e0e0e0;")
      h3.mb-0 Design System
      v-btn(icon @click="mobileMenuOpen = !mobileMenuOpen")
        v-icon mdi-menu

    .main-content.d-flex
      // Left Panel - Component Tabs
      .left-panel(:class="{ 'mobile-open': mobileMenuOpen }" class="pa-4")
        .d-flex.justify-space-between.align-center.mb-4
          h2.mb-0.d-none.d-md-block Design System
          v-btn.d-md-none(icon @click="mobileMenuOpen = false")
            v-icon mdi-close

        // Search Bar
        .search-bar.mb-4
          v-text-field(v-model="searchQuery" placeholder="Search components..." prepend-inner-icon="mdi-magnify" clearable dense outlined hide-details)

        .component-tabs
          .tab-item.mb-1.pa-2.cursor-pointer.round-lg(v-for="component in filteredComponents" :key="component.name" :class="{ active: isActiveComponent(component.name) }" @click="navigateToComponent(component.name); mobileMenuOpen = false")
            .d-flex.align-center
              span.text-sm {{ component.label }}

          // Empty State
          .empty-state.text-center.pa-4(v-if="filteredComponents.length === 0")
            p.text--secondary.mb-0 No components found

      // Overlay for mobile menu
      .mobile-overlay(v-if="mobileMenuOpen" @click="mobileMenuOpen = false" class="d-md-none")

      // Right Panel - Component Display
      .right-panel.flex-grow-1(:class="{ 'pa-3': $vuetify.breakpoint.smAndDown, 'pa-6': $vuetify.breakpoint.mdAndUp }")
        router-view

</template>

<script>
import { mapActions } from 'vuex'

export default {
  data() {
    return {
      mobileMenuOpen: false,
      searchQuery: '',
      components: [
        {
          name: 'typography',
          label: 'Typography'
        },
        {
          name: 'colors',
          label: 'Colors'
        },
        {
          name: 'button',
          label: 'Button'
        },
        {
          name: 'btn-toggle',
          label: 'Button Toggle'
        },
        {
          name: 'textfield',
          label: 'Text Field'
        },
        {
          name: 'textarea',
          label: 'Textarea'
        },
        {
          name: 'address-input',
          label: 'Address Input'
        },
        {
          name: 'phone-input',
          label: 'Phone Input'
        },
        {
          name: 'date-picker',
          label: 'Date Picker'
        },
        {
          name: 'radio',
          label: 'Radio'
        },
        {
          name: 'checkbox',
          label: 'Checkbox'
        },
        {
          name: 'switch',
          label: 'Switch'
        },
        {
          name: 'icon',
          label: 'Icon'
        },
        {
          name: 'search-button',
          label: 'Search Button'
        },
        {
          name: 'dialog',
          label: 'Dialog'
        },
        {
          name: 'delete-confirmation-dialog',
          label: 'Delete Confirmation Dialog'
        },
        {
          name: 'toast',
          label: 'Toast'
        },
        {
          name: 'menu',
          label: 'Menu'
        },
        {
          name: 'filter-menu',
          label: 'Filter Menu'
        },
        {
          name: 'dropdown',
          label: 'Dropdown'
        },
        {
          name: 'api-dropdown',
          label: 'API Dropdown'
        },
        {
          name: 'divider',
          label: 'Divider'
        },
        {
          name: 'card',
          label: 'Card'
        },
        {
          name: 'badge',
          label: 'Badge'
        },
        {
          name: 'dot',
          label: 'Dot'
        },
        {
          name: 'radio-selection-card',
          label: 'Radio selection card'
        },
        {
          name: 'checkbox-selection-card',
          label: 'Checkbox selection card'
        },
        {
          name: 'tooltip',
          label: 'Tooltip'
        },
        {
          name: 'empty-state-card',
          label: 'Empty State Card'
        },
        {
          name: 'truncated-list',
          label: 'Truncated List'
        },
        {
          name: 'pagination',
          label: 'Pagination'
        },
        {
          name: 'img',
          label: 'Image'
        },
        {
          name: 'avatar',
          label: 'Avatar'
        },
        {
          name: 'responsive',
          label: 'Responsive'
        },
        {
          name: 'data-table',
          label: 'Data Table'
        },
        {
          name: 'upload-button',
          label: 'Upload Button'
        },
        {
          name: 'upload-card',
          label: 'Upload Card'
        },
        {
          name: 'upload-qr-databin',
          label: 'Upload QR Databin'
        },
        {
          name: 'file-preview',
          label: 'File Preview'
        },
        {
          name: 'tabs',
          label: 'Tabs'
        },
        {
          name: 'progress-linear',
          label: 'Progress Linear'
        },
        {
          name: 'progress-circular',
          label: 'Progress Circular'
        }
      ]
    }
  },
  computed: {
    filteredComponents() {
      if (!this.searchQuery) {
        return this.components
      }
      const query = this.searchQuery.toLowerCase().trim()
      return this.components.filter(component =>
        component.label.toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    this.$store.commit('ui/setDisplaySize', 'large')
  },
  methods: {
    ...mapActions(['showMessage']),
    navigateToComponent(componentName) {
      this.$router.push(`/_design_docs/components/${componentName}`)
    },
    isActiveComponent(componentName) {
      // Check if the current route matches the component exactly
      const currentPath = this.$route.path
      return currentPath === `/_design_docs/components/${componentName}`
    }
  }
}
</script>

<style scoped>
.design-system-docs {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  position: relative;
}

.mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.left-panel {
  width: 280px;
  height: 100dvh;
  background-color: #f8f9fa;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  flex-shrink: 0;
  position: fixed;
}

.right-panel {
  height: 100dvh;
  margin-left: 290px;
}

.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* Mobile Responsive Styles */
@media (max-width: 959px) {
  .design-system-docs {
    position: relative;
  }

  .main-content {
    margin-top: 65px;
  }

  .left-panel {
    position: fixed;
    top: 0;
    left: -280px;
    width: 280px;
    height: 100vh;
    z-index: 1000;
    transition: left 0.3s ease-in-out;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }

  .left-panel.mobile-open {
    left: 0;
  }

  .right-panel {
    width: 100%;
    margin-left: 0;
    height: calc(100dvh - 65px);
  }
}

/* Tablet adjustments */
@media (max-width: 1200px) and (min-width: 960px) {
  .left-panel {
    width: 260px;
  }
}

.tab-item {
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.tab-item:hover {
  background-color: #ffffff;
  border-color: #e0e0e0;
}

.tab-item.active {
  background-color: #1976d2;
  color: white;
  border-color: #1976d2;
}

.tab-item.active .v-icon {
  color: white !important;
}

.cursor-pointer {
  cursor: pointer;
}

.h-100vh {
  height: 100vh;
}
</style>
